/**
 * Display names and type unions shared by the redesigned settings screens.
 *
 * WHY THIS FILE EXISTS. In the preview kit these live in the per-screen
 * `*.data.ts` modules alongside the mock `SettingsSection[]` fixtures. The
 * fixtures must not ship - they carry sample feed titles, sample account
 * handles and "not built in this round" placeholder strings - but the labels
 * and unions below are product decisions, not mocks, and the screens import
 * them. So the kit's data modules split in two on the way over: the fixtures
 * stay behind, this part comes across.
 *
 * KEEP IT IN STEP. Each label is a ruling recorded in LEGACY-QUESTIONS, and
 * the kit is where those rulings are reviewed. If a name changes here, change
 * it in the kit's `feed-settings.data.ts` / `social-settings.data.ts` too, or
 * the next screen copied over will quietly reintroduce the old one.
 */

import type { NetworkSlug } from "@/lib/networks"

/**
 * The source an automation pulls from. Legacy's `INPUT_TYPE_*`
 * (`code/classes/types.php:136`): FEED 100, TWITTER 104, PINTEREST 106,
 * INSTAGRAM_BUSINESS 107, FACEBOOK_PAGE 108, TIKTOK 110, YOUTUBE 111.
 */
export type SourceType =
  | "rss"
  | "twitter"
  | "instagram"
  | "facebook"
  | "tiktok"
  | "youtube"

export const SOURCE_LABEL: Record<SourceType, string> = {
  rss: "RSS feed",
  twitter: "X",
  instagram: "Instagram Professional",
  facebook: "Facebook Page",
  tiktok: "TikTok",
  youtube: "YouTube",
}

/**
 * PINTEREST IS NOT A SOURCE TYPE. It is `INPUT_SUBTYPE_PINTEREST = 10`, a
 * subtype of `INPUT_TYPE_FEED` (`types.php:158`), so a Pinterest automation
 * has `type = 100` and behaves as RSS everywhere: Feed URL slot, full Advanced
 * tab, photo posting shown.
 *
 * A subtype changes the LABEL and the URL example, nothing behavioural.
 */
export type FeedSubtype =
  | "pinterest"
  | "ebay"
  | "etsy"
  | "medium"
  | "shopify"
  | "squarespace"
  | "vimeo"
  | "youtube"
  | "substack"

export const SUBTYPE_LABEL: Record<FeedSubtype, string> = {
  pinterest: "Pinterest",
  ebay: "eBay",
  etsy: "Etsy",
  medium: "Medium",
  shopify: "Shopify",
  squarespace: "Squarespace",
  vimeo: "Vimeo",
  youtube: "YouTube",
  substack: "Substack",
}

/** What the header's meta line calls this source. */
export function sourceLabel(source: SourceType, subtype?: FeedSubtype) {
  return source === "rss" && subtype
    ? SUBTYPE_LABEL[subtype]
    : SOURCE_LABEL[source]
}

/**
 * The brand badge for a source, as `<Networks>` keys it: the same mark Create
 * Automation shows. A feed subtype with a mark of its own shows that (a
 * Pinterest feed shows Pinterest); Medium and Squarespace have none, so they
 * read as the plain feed they are.
 */
const SUBTYPE_SLUG: Record<FeedSubtype, NetworkSlug> = {
  pinterest: "pinterest",
  ebay: "ebay",
  etsy: "etsy",
  medium: "rss",
  shopify: "shopify",
  squarespace: "rss",
  vimeo: "vimeo",
  youtube: "youtube",
  substack: "substack",
}

const SOURCE_SLUG: Record<SourceType, NetworkSlug> = {
  rss: "rss",
  twitter: "x",
  instagram: "instagram",
  facebook: "facebook",
  tiktok: "tiktok",
  youtube: "youtube",
}

export function sourceNetwork(source: SourceType, subtype?: FeedSubtype): NetworkSlug {
  return source === "rss" && subtype ? SUBTYPE_SLUG[subtype] : SOURCE_SLUG[source]
}

/** Every destination network, keyed as the screens key them. */
export type DestinationNetwork =
  | "twitter"
  | "facebook"
  | "tumblr"
  | "linkedin"
  | "pinterest"
  | "feed"
  | "instagramBusiness"
  | "gmyBusiness"
  | "stocktwits"
  | "slack"
  | "discord"
  | "blogger"
  | "wordpress"
  | "reddit"
  | "mastodon"
  | "tiktok"
  | "bluesky"
  | "nextdoor"
  | "youtube"
  | "threads"
  | "snapchat"

/**
 * `POST_SVC_*` from `code/classes/types.php`, in its order. Carried so mapping
 * a stored output to its screen is mechanical rather than a second list to
 * keep in step.
 */
export const NETWORK_PSID: Record<DestinationNetwork, number> = {
  twitter: 1,
  facebook: 2,
  tumblr: 3,
  linkedin: 4,
  pinterest: 5,
  feed: 7,
  instagramBusiness: 8,
  gmyBusiness: 20,
  stocktwits: 22,
  slack: 23,
  discord: 24,
  blogger: 30,
  wordpress: 35,
  reddit: 36,
  mastodon: 37,
  tiktok: 38,
  bluesky: 39,
  nextdoor: 40,
  youtube: 41,
  threads: 42,
  snapchat: 43,
}

/** Every network, in `types.php` order. */
export const NETWORKS = Object.keys(NETWORK_PSID) as DestinationNetwork[]

/**
 * These are NOT `POST_SVC_NAMES`. Three differ deliberately, each an Emily
 * ruling recorded in LEGACY-QUESTIONS: "Google Business Profile" (Google
 * renamed the product and "GMyBusiness" never matched it), "WordPress" (the
 * brand's own capitalisation, vs `POST_SVC_NAMES`' "Wordpress"), and "X".
 *
 * "Feed" is flagged rather than renamed - it is legacy's own name for this
 * destination type and also the noun the naming work wants to retire. See
 * LEGACY-QUESTIONS B2.
 */
export const NETWORK_LABEL: Record<DestinationNetwork, string> = {
  twitter: "X",
  facebook: "Facebook",
  tumblr: "Tumblr",
  linkedin: "LinkedIn",
  pinterest: "Pinterest",
  feed: "Feed",
  instagramBusiness: "Instagram Business",
  gmyBusiness: "Google Business Profile",
  stocktwits: "StockTwits",
  slack: "Slack",
  discord: "Discord",
  blogger: "Blogger",
  wordpress: "WordPress",
  reddit: "Reddit",
  mastodon: "Mastodon",
  tiktok: "TikTok",
  bluesky: "Bluesky",
  nextdoor: "Nextdoor",
  youtube: "YouTube",
  threads: "Threads",
  snapchat: "Snapchat",
}

/**
 * The brand badge for a destination, as `<Networks>` keys it. `null` where the
 * registry has no artwork - Google Business Profile - which then shows no mark.
 */
export const DESTINATION_SLUG: Record<DestinationNetwork, NetworkSlug | null> = {
  twitter: "x",
  facebook: "facebook",
  tumblr: "tumblr",
  linkedin: "linkedin",
  pinterest: "pinterest",
  feed: "rss",
  instagramBusiness: "instagram",
  gmyBusiness: null,
  stocktwits: "stocktwits",
  slack: "slack",
  discord: "discord",
  blogger: "blogger",
  wordpress: "wordpress",
  reddit: "reddit",
  mastodon: "mastodon",
  tiktok: "tiktok",
  bluesky: "bluesky",
  nextdoor: "nextdoor",
  youtube: "youtube",
  threads: "threads",
  snapchat: "snapchat",
}
